import { Add as AddIcon, Edit as EditIcon, Sync as SyncIcon } from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
} from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import PartnerCommissionApi from "../../../infrastructure/api/partnerCommissionApi";
import { getEnumLabel } from "../SLN/market-growth/constants/commissionEnums";
import {
  PARTNER_STATUS_LABELS,
  getPartnerStatusChipColor,
} from "../SLN/market-growth/constants/partnerEnums";
import CreateSubPartnerDialog from "./components/CreateSubPartnerDialog";
import CopyTextField from "./components/CopyTextField";
import PartnerCommissionPageShell from "./components/PartnerCommissionPageShell";
import UpdateTeamShareDialog from "./components/UpdateTeamShareDialog";
import { TEAM_ACCESS_DENIED_MESSAGE } from "./constants/portalEnums";
import { formatMoney } from "./utils/formatMoney";
import { canManageTeam } from "./utils/portalHelpers";
import { buildPartnerJoinLink } from "../SLN/market-growth/constants/partnerEnums";

const PartnerTeamPage = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [noPartnerProfile, setNoPartnerProfile] = useState(false);
  const [profile, setProfile] = useState(null);
  const [items, setItems] = useState([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [shareDialog, setShareDialog] = useState({ open: false, member: null });
  const [saving, setSaving] = useState(false);

  const teamAllowed = canManageTeam(profile);
  const partnerJoinLink = teamAllowed
    ? buildPartnerJoinLink(profile?.referralCode)
    : "";

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    setNoPartnerProfile(false);
    try {
      const [profileRes, teamRes] = await Promise.all([
        PartnerCommissionApi.getProfile(),
        PartnerCommissionApi.getTeam(),
      ]);

      const profileNorm = PartnerCommissionApi.normalize(
        profileRes,
        "بارگذاری پروفایل ناموفق بود."
      );
      if (profileNorm.noPartnerProfile) {
        setNoPartnerProfile(true);
        setProfile(null);
        setItems([]);
        return;
      }
      if (profileNorm.error) {
        setError(profileNorm.error);
        setProfile(null);
        setItems([]);
        return;
      }

      const teamNorm = PartnerCommissionApi.normalize(teamRes, "بارگذاری تیم ناموفق بود.");
      if (teamNorm.noPartnerProfile) {
        setNoPartnerProfile(true);
        return;
      }
      if (teamNorm.error && !teamNorm.isUnauthorized) {
        setError(teamNorm.error);
      }

      setProfile(profileNorm.data);
      setItems(Array.isArray(teamNorm.data) ? teamNorm.data : []);
    } catch (e) {
      setError(e?.message || "خطا در بارگذاری");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleCreate = async (payload) => {
    setSaving(true);
    try {
      const res = await PartnerCommissionApi.createSubPartner(payload);
      const normalized = PartnerCommissionApi.normalize(res, "ایجاد زیرمجموعه ناموفق بود.");
      if (normalized.error) return { error: normalized.error };
      toast.success(res?.message || res?.Message || "زیرمجموعه با موفقیت ایجاد شد.");
      setCreateOpen(false);
      await load();
      return {};
    } catch (e) {
      return { error: e?.message || "خطا در ذخیره" };
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateShare = async (payload) => {
    if (!shareDialog.member) return {};
    setSaving(true);
    try {
      const res = await PartnerCommissionApi.updateTeamShare(
        shareDialog.member.partnerId,
        payload
      );
      const normalized = PartnerCommissionApi.normalize(res, "به‌روزرسانی سهم ناموفق بود.");
      if (normalized.error) return { error: normalized.error };
      toast.success("سهم پورسانت به‌روزرسانی شد.");
      setShareDialog({ open: false, member: null });
      await load();
      return {};
    } catch (e) {
      return { error: e?.message || "خطا در ذخیره" };
    } finally {
      setSaving(false);
    }
  };

  const showTeamContent = !loading && !noPartnerProfile && !error;

  return (
    <PartnerCommissionPageShell
      title="تیم من"
      description="لیست زیرمجموعه‌ها و سهم پورسانت."
      loading={loading}
      error={error}
      noPartnerProfile={noPartnerProfile}
      onRetry={load}
    >
      {showTeamContent && !teamAllowed ? (
        <Alert severity="warning">{TEAM_ACCESS_DENIED_MESSAGE}</Alert>
      ) : null}

      {showTeamContent && teamAllowed ? (
        <>
          {partnerJoinLink ? (
            <CopyTextField
              label="لینک ثبت‌نام زیرمجموعه (عمومی)"
              value={partnerJoinLink}
              copyMessage="لینک ثبت‌نام زیرمجموعه کپی شد."
            />
          ) : null}
          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2, mt: partnerJoinLink ? 2 : 0 }}>
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)}>
              ایجاد زیرمجموعه
            </Button>
            <Tooltip title="بارگذاری مجدد">
              <IconButton onClick={load}>
                <SyncIcon />
              </IconButton>
            </Tooltip>
          </Box>

          <Card variant="outlined">
            <CardContent sx={{ overflowX: "auto", p: 0 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>نام / کاربر</TableCell>
                    <TableCell>موبایل</TableCell>
                    <TableCell>وضعیت</TableCell>
                    <TableCell>کد معرف</TableCell>
                    <TableCell>سهم (%)</TableCell>
                    <TableCell>کل پورسانت</TableCell>
                    <TableCell>پرداخت‌شده</TableCell>
                    <TableCell align="center">عملیات</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} align="center">
                        هنوز عضوی در تیم شما ثبت نشده است.
                      </TableCell>
                    </TableRow>
                  ) : (
                    items.map((row) => (
                      <TableRow key={row.partnerId} hover>
                        <TableCell>
                          {row.displayName || "—"}
                          <Box component="span" sx={{ color: "text.secondary", ml: 0.5 }}>
                            ({row.userId})
                          </Box>
                        </TableCell>
                        <TableCell>{row.mobileNumber || "—"}</TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={getEnumLabel(PARTNER_STATUS_LABELS, row.status)}
                            color={getPartnerStatusChipColor(row.status)}
                          />
                        </TableCell>
                        <TableCell>{row.referralCode || "—"}</TableCell>
                        <TableCell>{row.currentSharePercent ?? "—"}</TableCell>
                        <TableCell>{formatMoney(row.totalCommissionGenerated)}</TableCell>
                        <TableCell>{formatMoney(row.totalPaidCommission)}</TableCell>
                        <TableCell align="center">
                          <Tooltip title="تنظیم سهم">
                            <IconButton
                              size="small"
                              onClick={() => setShareDialog({ open: true, member: row })}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </>
      ) : null}

      <CreateSubPartnerDialog
        open={createOpen}
        onClose={() => !saving && setCreateOpen(false)}
        onSubmit={handleCreate}
        saving={saving}
        parentProfile={profile}
      />

      <UpdateTeamShareDialog
        open={shareDialog.open}
        onClose={() => !saving && setShareDialog({ open: false, member: null })}
        member={shareDialog.member}
        onSubmit={handleUpdateShare}
        saving={saving}
      />
    </PartnerCommissionPageShell>
  );
};

export default PartnerTeamPage;
